// Admin session — token stored in localStorage. Expires after 8 hours per
// API contract. The token doubles as the admin identity for audit purposes.

import { api } from '@/lib/api.ts';
import { env } from '@/lib/env.ts';

const TOKEN_KEY = 'okn:admin:token';
const EXPIRES_KEY = 'okn:admin:expiresAt';

export type AdminSession = {
  token: string;
  expiresAt: string;
};

export function getAdminSession(): AdminSession | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAt = localStorage.getItem(EXPIRES_KEY);
    if (!token || !expiresAt) return null;
    if (new Date(expiresAt).getTime() < Date.now()) return null;
    return { token, expiresAt };
  } catch {
    return null;
  }
}

export function setAdminSession(s: AdminSession) {
  localStorage.setItem(TOKEN_KEY, s.token);
  localStorage.setItem(EXPIRES_KEY, s.expiresAt);
}

export function clearAdminSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

export async function adminApi<T>(
  path: string,
  opts: Omit<Parameters<typeof api>[1] & object, never> = {},
): Promise<T> {
  const s = getAdminSession();
  return api<T>(path, {
    ...opts,
    headers: {
      ...(opts.headers ?? {}),
      ...(s ? { Authorization: `Bearer ${s.token}` } : {}),
    },
  });
}

/**
 * Download a binary file (e.g. the roster PDF) from an admin-auth endpoint.
 * Fetches with the Bearer token, then triggers a browser download — a plain
 * <a href> can't carry the auth header.
 */
export async function downloadAdminFile(path: string, filename: string): Promise<void> {
  const s = getAdminSession();
  const res = await fetch(`${env.VITE_API_BASE_URL}${path}`, {
    headers: s ? { Authorization: `Bearer ${s.token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export type LoginResponse = {
  token: string;
  expiresAt: string;
  admin: { id: string; name: string };
};

export async function adminLogin(email: string, password: string): Promise<LoginResponse> {
  return api<LoginResponse>('/admin/dev-login', {
    method: 'POST',
    body: { email, password },
  });
}

export type AdminMe = { id: string; name: string; exp: number };
export async function fetchAdminMe(): Promise<AdminMe> {
  return adminApi<AdminMe>('/admin/me');
}
